import { Command } from 'commander'
import pc from 'picocolors'
import { existsSync, readFileSync, readdirSync, statSync } from 'fs'
import { join, relative } from 'path'
import { loadConfig } from '../../config'

const DEFAULT_ENDPOINT = 'https://brixon-presentations.brixon-group.workers.dev/upload'

export const deployCommand = new Command('deploy')
  .description('Deploy the presentation to Brixon platform')
  .option('-k, --api-key <key>', 'API key (or set BRIXON_API_KEY env)')
  .option('-e, --endpoint <url>', 'Upload endpoint', DEFAULT_ENDPOINT)
  .option('--dry-run', 'Show what would be uploaded without uploading')
  .action(async (options: { apiKey?: string; endpoint: string; dryRun?: boolean }) => {
    const cwd = process.cwd()

    console.log(pc.cyan('\nBrixon Deploy'))
    console.log(pc.dim('─'.repeat(40)))

    // Get API key
    const apiKey = options.apiKey || process.env.BRIXON_API_KEY
    if (!apiKey) {
      console.error(pc.red('\nError: API key required.'))
      console.error(pc.dim('Use --api-key flag or set BRIXON_API_KEY environment variable.'))
      process.exit(1)
    }

    // Load config
    const config = await loadConfig(cwd)
    if (!config) {
      console.error(pc.red('\nError: No brixon.config.ts found.'))
      console.error(pc.dim('Run `brixon build` first.'))
      process.exit(1)
    }

    // Check for dist directory
    const distDir = join(cwd, config.build?.outDir || 'dist')
    if (!existsSync(distDir)) {
      console.error(pc.red('\nError: No dist directory found.'))
      console.error(pc.dim('Run `brixon build` first.'))
      process.exit(1)
    }

    // Check for manifest
    const manifestPath = join(distDir, 'manifest.json')
    if (!existsSync(manifestPath)) {
      console.error(pc.red('\nError: No manifest.json found in dist.'))
      console.error(pc.dim('Run `brixon build` to generate it.'))
      process.exit(1)
    }

    // Read manifest
    const manifest = JSON.parse(readFileSync(manifestPath, 'utf-8'))
    const slug = manifest.slug

    console.log(pc.white(`\nDeploying: ${pc.bold(manifest.title)}`))
    console.log(pc.dim(`Slug: ${slug}`))

    // Collect files to upload
    const files = collectFiles(distDir)
    console.log(pc.dim(`Files: ${files.length}`))

    if (options.dryRun) {
      console.log(pc.yellow('\n[Dry run] Would upload:'))
      files.forEach((f) => console.log(pc.dim(`  - ${f.relativePath}`)))
      console.log(pc.yellow('\nNo files were uploaded.'))
      return
    }

    // Create form data
    const formData = new FormData()

    // Add manifest
    const manifestBlob = new Blob([readFileSync(manifestPath, 'utf-8')], {
      type: 'application/json',
    })
    formData.append('manifest', manifestBlob, 'manifest.json')

    // Add all other files
    for (const file of files) {
      if (file.relativePath === 'manifest.json') continue
      const content = readFileSync(file.absolutePath)
      const blob = new Blob([content])
      formData.append('files', blob, file.relativePath)
    }

    console.log(pc.dim('\nUploading...'))

    try {
      const response = await fetch(options.endpoint, {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.error(pc.red(`\nError: ${error.error || 'Upload failed'}`))
        process.exit(1)
      }

      const result = await response.json()

      console.log(pc.green('\n✓ Deployed successfully!'))
      // Show full URL by combining endpoint base with result path
      const baseUrl = options.endpoint.replace('/upload', '')
      const fullUrl = result.url.startsWith('http') ? result.url : `${baseUrl}${result.url}`
      console.log(pc.dim(`\nURL: ${fullUrl}`))
      console.log(pc.dim(`Files: ${result.filesUploaded}`))
      console.log(pc.dim(`Time: ${result.deployedAt}`))
    } catch (error) {
      console.error(pc.red('\nError: Failed to connect to upload endpoint.'))
      console.error(pc.dim(String(error)))
      process.exit(1)
    }
  })

interface FileInfo {
  absolutePath: string
  relativePath: string
}

function collectFiles(dir: string, baseDir: string = dir): FileInfo[] {
  const files: FileInfo[] = []

  const entries = readdirSync(dir)
  for (const entry of entries) {
    const absolutePath = join(dir, entry)
    const relativePath = relative(baseDir, absolutePath)

    if (statSync(absolutePath).isDirectory()) {
      files.push(...collectFiles(absolutePath, baseDir))
    } else {
      files.push({ absolutePath, relativePath })
    }
  }

  return files
}
